import { ReactNode } from 'react';
import { currency } from '../formatters';

// Indemnity payout waterfall — the grid rows shared by every indemnity-capable
// per-cell payout tooltip. Indemnity pays a fixed cash benefit regardless of
// actual cost, so the reimbursement-style "eligible cost → over-cap / stranded-
// day buckets" breakdown is the wrong frame: the cash (plus any drained surplus
// reserve) simply gets applied to cost, and cash above the cost banks as
// surplus. Modeling it as a cash waterfall keeps every line reconciling — unlike
// the day-bucket breakdown, where on a partial benefit-period boundary row the
// indemnity cash can exceed the period-clamped eligible cost and a sub-bucket
// ends up larger than the out-of-pocket subtotal it itemizes.
//
// All figures come straight off the engine's payout deltas:
//   indemnityPayout  — gross cash paid this row
//   ltcCoveragePaid  — cash applied directly to cost (capped at eligible cost)
//   ltcPayoutToCost  — total applied to cost (direct + surplus reserve drained)
// Per row exactly one of the two surplus movements is non-zero (the reserve
// either drains into a shortfall or banks the excess — never both), so the two
// conditional lines are mutually exclusive:
//   surplus applied = ltcPayoutToCost − ltcCoveragePaid   (reserve → cost)
//   banked surplus  = indemnityPayout − ltcCoveragePaid   (cash above cost → reserve)
// Room & board (Independent Living only; 0 elsewhere) is fungible-cash covered:
// this month's benefit above the eligible care cost — the amount that would
// otherwise bank as surplus — covers room & board before banking
// (`payout.roomAndBoardCovered`), so what actually banks = gross excess −
// covered. Net out-of-pocket = care shortfall (`ineligibleCost`) + the room &
// board the cash did NOT meet (`roomAndBoardCost − roomAndBoardCovered`). Non-IL
// rows carry 0 room & board and 0 covered, so they stay byte-identical.
//
// `cashNote` is an optional small annotation on the "Indemnity cash paid" line,
// used by STC to explain a pro-rated cash benefit (the monthly cap is scaled to
// the payable days remaining in the lifetime benefit period — e.g. a boundary
// month pays cap × 15/30 when only 15 benefit-period days are left).
export function indemnityPayoutWaterfall(month: MonthlyEntry, cashNote?: string): ReactNode {
  const cost = month.facts.effectiveCareCost;
  const indemnityCash = month.deltas.payout.indemnityPayout;
  const directlyApplied = month.deltas.payout.ltcCoveragePaid;
  const appliedToCost = month.deltas.payout.ltcPayoutToCost;
  const surplusApplied = Math.max(appliedToCost - directlyApplied, 0);
  // A retroactive waiting-period lump also lands against this row's cost on
  // strategies that pay one (SecureCare IV). It is not part of `indemnityPayout`
  // or `ltcPayoutToCost`, so it has to be itemized separately or the waterfall
  // stops reconciling to the engine's out-of-pocket figure.
  const epDeltas = month.deltas.strategyDeltas as
    | { epLumpSumPaid?: number; epLumpSumAppliedToCost?: number }
    | undefined;
  const epLumpPaid = Math.max(epDeltas?.epLumpSumPaid ?? 0, 0);
  const epLumpApplied = Math.max(epDeltas?.epLumpSumAppliedToCost ?? 0, 0);
  // Care shortfall mirrors the engine's
  // `ineligibleCost = cost − ltcPayoutToCost − epLumpSumAppliedToCost`, computed
  // rather than read off the payout so the waterfall still renders for callers
  // that build a partial payout object.
  const careShortfall = Math.max(cost - appliedToCost - epLumpApplied, 0);
  // Room & board (Independent Living only; 0 elsewhere). Indemnity cash is
  // fungible, so this month's excess covers room & board before banking; only
  // the unmet remainder is out-of-pocket. `roomAndBoardCovered` is the engine's
  // diversion — the gross excess that would otherwise bank as surplus, capped at
  // the room & board cost — so what actually banks = gross excess − covered.
  const roomAndBoard = month.facts.roomAndBoardCost ?? 0;
  const roomAndBoardCovered = month.deltas.payout.roomAndBoardCovered ?? 0;
  const roomAndBoardOop = Math.max(roomAndBoard - roomAndBoardCovered, 0);
  // Banked surplus = indemnity excess (after cash diverted to room & board) plus
  // any EP-lump excess. The lump is typically ~3× a monthly cap against one
  // month of cost, so most of it banks; counting only the indemnity stream would
  // leave the bulk of the row's cash unaccounted for against the headline payout.
  const surplusBanked =
    Math.max(indemnityCash - directlyApplied - roomAndBoardCovered, 0) +
    Math.max(epLumpPaid - epLumpApplied, 0);
  const netOop = careShortfall + roomAndBoardOop;

  return (
    <>
      <span className="text-gray-300">Care cost</span>
      <span className="font-semibold tabular-nums">{currency(cost)}</span>
      <span className="text-gray-300">
        Indemnity cash paid
        {cashNote ? <span className="text-gray-500"> · {cashNote}</span> : null}
      </span>
      <span className="font-semibold tabular-nums">{currency(indemnityCash)}</span>
      {surplusApplied > 0 && (
        <>
          <span className="pl-2 text-gray-400">+ surplus reserve applied</span>
          <span className="tabular-nums text-emerald-300">+{currency(surplusApplied)}</span>
        </>
      )}
      {epLumpPaid > 0 && (
        <>
          <span className="text-gray-300">WP reimbursement paid</span>
          <span className="font-semibold tabular-nums text-amber-300">
            {currency(epLumpPaid)}
          </span>
        </>
      )}
      <span className="text-gray-300">Applied to cost</span>
      <span className="font-semibold tabular-nums">
        {currency(appliedToCost + epLumpApplied)}
      </span>
      {roomAndBoardCovered > 0 && (
        <>
          <span className="text-gray-300">Cash applied to room &amp; board</span>
          <span className="font-semibold tabular-nums">{currency(roomAndBoardCovered)}</span>
        </>
      )}
      {surplusBanked > 0 && (
        <>
          <span className="text-gray-300">Banked as surplus</span>
          <span className="font-semibold tabular-nums text-emerald-300">
            +{currency(surplusBanked)}
          </span>
        </>
      )}
      <span className="text-gray-300">Net out of pocket</span>
      <span className="font-semibold tabular-nums text-rose-300">{currency(netOop)}</span>
      {roomAndBoard > 0 && (
        <>
          <span className="pl-2 text-gray-400">· care shortfall</span>
          <span className="tabular-nums text-rose-300">{currency(careShortfall)}</span>
          <span className="pl-2 text-gray-400">
            · room &amp; board{roomAndBoardCovered > 0 ? ' (after cash)' : ''}
          </span>
          <span className="tabular-nums text-rose-300">{currency(roomAndBoardOop)}</span>
        </>
      )}
    </>
  );
}
